// BEAD Territory Validation Report — the executive view.
// Pulls the project's BEAD metadata + plant BOM + GeoHub passings,
// renders a print-friendly single-page report.
// "Print" button uses window.print() with custom @media print CSS.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import maplibregl from 'maplibre-gl';
import { api } from '../lib/api';
import { BASEMAPS } from '../lib/map-basemaps';
import type { BomResult } from '../types/bom';
import type { Plant, PlantGraph, Project } from '../types/plant';

const fmtUSD = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000   ? `$${(n / 1_000).toFixed(0)}k`
  : `$${Math.round(n).toLocaleString()}`;
const fmtNum = (n: number) => Math.round(n).toLocaleString();

export default function BeadReportPage() {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects'),
  });
  useEffect(() => {
    if (selectedProjectId) return;
    // Default to the first BEAD-tagged project, else first project.
    const beadFirst = (projects as any[]).find((p) => p.subgrant_amount > 0);
    if (beadFirst) setSelectedProjectId(beadFirst.id);
    else if (projects[0]) setSelectedProjectId(projects[0].id);
  }, [projects, selectedProjectId]);

  const project = projects.find((p) => p.id === selectedProjectId) as any | undefined;

  const { data: plants = [] } = useQuery<Plant[]>({
    queryKey: ['plants', selectedProjectId],
    queryFn: () => selectedProjectId ? api.get(`/plants/project/${selectedProjectId}`) : Promise.resolve([]),
    enabled: !!selectedProjectId,
  });
  const plant = plants[0];

  const { data: bom } = useQuery<BomResult>({
    queryKey: ['bom', plant?.id],
    queryFn: () => plant ? api.get(`/bom/plant/${plant.id}`) : Promise.reject(),
    enabled: !!plant,
  });

  const { data: graph } = useQuery<PlantGraph>({
    queryKey: ['plant-graph', plant?.id],
    queryFn: () => plant ? api.get(`/plants/${plant.id}/graph`) : Promise.reject(),
    enabled: !!plant,
  });

  const { data: passings } = useQuery<{ buffer: any; passings: any; buffer_ft: number; count: number }>({
    queryKey: ['plant-passings', plant?.id],
    queryFn: () => plant ? api.get(`/plants/${plant.id}/passings`) : Promise.reject(),
    enabled: !!plant,
  });

  const { data: beadEligible } = useQuery<{ location_ids: string[] }>({
    queryKey: ['bead-eligible', selectedProjectId],
    queryFn: () => selectedProjectId ? api.get(`/bead/project/${selectedProjectId}/eligible-bsls`) : Promise.resolve({ location_ids: [] }),
    enabled: !!selectedProjectId,
  });

  // Committed expansion zones — Layer 3 real data (replaces the +50%/+25% stub).
  const { data: expansionZones = [] } = useQuery<any[]>({
    queryKey: ['expansion-zones', selectedProjectId],
    queryFn: () => selectedProjectId ? api.get(`/expansion/project/${selectedProjectId}`) : Promise.resolve([]),
    enabled: !!selectedProjectId,
  });
  const committedZones = expansionZones.filter((z) => z.status === 'committed');

  // Layer comparison maths — Layer 3 now pulls real committed expansion zones.
  const layers = useMemo(() => {
    if (!bom) return null;
    const grant = bom.subgrant_amount;
    const budget = bom.budget_total;
    const elig = bom.passings_eligible;
    const opp = bom.passings_opportunistic;
    const total = bom.total_cost;

    // Aggregate committed zones into a Layer 3 delta. Each zone's analysis holds
    // its extra passings, incremental capex, and projected 5-yr contribution.
    const l3Delta = committedZones.reduce((acc, z) => {
      const a = z.analysis ?? {};
      const p = a.passings ?? {};
      const sellable = Number(p.served ?? 0) + Number(p.unknown ?? 0); // already-served + addressable; BEAD-eligible in zone rolls into L1
      return {
        homes: acc.homes + sellable,
        capex: acc.capex + Number(a.incremental_capex ?? 0),
        contribution: acc.contribution + Number(a.contribution_horizon ?? 0),
        bead_eligible_extra: acc.bead_eligible_extra + Number(p.bead_eligible ?? 0),
      };
    }, { homes: 0, capex: 0, contribution: 0, bead_eligible_extra: 0 });

    const hasL3 = committedZones.length > 0;

    return {
      l1: {
        homes: elig,
        cost: total,
        cph: elig ? total / elig : 0,
        grant_funded: grant,
        net_capex: total - grant,
        pencils: total <= budget,
      },
      l1_l2: {
        homes: elig + opp,
        cost: total,
        cph: (elig + opp) ? total / (elig + opp) : 0,
        grant_funded: grant,
        net_capex: total - grant,
        revenue_pv: opp * 85 * 0.35 * 60,
      },
      l1_l2_l3: hasL3 ? {
        homes: elig + opp + l3Delta.homes,
        cost: total + l3Delta.capex,
        cph: (elig + opp + l3Delta.homes) ? (total + l3Delta.capex) / (elig + opp + l3Delta.homes) : 0,
        grant_funded: grant,      // BEAD stays the same; L3 is private capex
        net_capex: (total + l3Delta.capex) - grant,
        revenue_pv: (opp * 85 * 0.35 * 60) + l3Delta.contribution,
        zone_count: committedZones.length,
        l3_capex: l3Delta.capex,
        l3_homes: l3Delta.homes,
        is_estimate: false,
      } : {
        homes: Math.round((elig + opp) * 1.5),
        cost: total * 1.25,
        cph: ((elig + opp) * 1.5) ? (total * 1.25) / ((elig + opp) * 1.5) : 0,
        grant_funded: grant,
        net_capex: total * 1.25 - grant,
        revenue_pv: Math.round((elig + opp) * 0.5) * 85 * 0.45 * 60,
        zone_count: 0,
        l3_capex: 0,
        l3_homes: 0,
        is_estimate: true,
      },
    };
  }, [bom, committedZones]);

  // ---- Mini map (read-only preview of plant + BSLs colored by eligibility) ----
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  // NOTE: this effect must depend on `project` because the container <div> is
  // only rendered after the "if (!project) return Loading" guard. With empty
  // deps the effect fires once on mount, when containerRef is still null, and
  // never gets another shot — leaving the map blank. Depending on project forces
  // it to re-run once the UI (and the ref) are actually in the DOM.
  useEffect(() => {
    if (!project || !containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASEMAPS.satellite.style,
      center: [-81.19, 37.78],
      zoom: 13,
      hash: false,
      attributionControl: false,
      interactive: true,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;
    if (map.loaded()) setMapReady(true);
    else map.on('load', () => setMapReady(true));
    return () => { map.remove(); mapRef.current = null; setMapReady(false); };
  }, [project]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !graph || !passings) return;
    const eligibleSet = new Set((beadEligible?.location_ids ?? []).map(String));

    if (!map.getSource('rep-segs')) {
      map.addSource('rep-segs', { type: 'geojson', data: graph.segments } as any);
      map.addLayer({
        id: 'rep-segs', type: 'line', source: 'rep-segs',
        paint: { 'line-color': '#dc2626', 'line-width': 2, 'line-opacity': 0.95 },
      });
      map.addSource('rep-buf', { type: 'geojson', data: passings.buffer } as any);
      map.addLayer({
        id: 'rep-buf', type: 'fill', source: 'rep-buf',
        paint: { 'fill-color': '#22d3ee', 'fill-opacity': 0.10 },
      }, 'rep-segs');
      const tagged = {
        type: 'FeatureCollection' as const,
        features: (passings.passings.features ?? []).map((f: any) => ({
          ...f,
          properties: { ...(f.properties ?? {}), eligible: eligibleSet.has(String(f.properties?.location_id ?? f.id)) },
        })),
      };
      map.addSource('rep-dots', { type: 'geojson', data: tagged } as any);
      map.addLayer({
        id: 'rep-dots', type: 'circle', source: 'rep-dots',
        paint: {
          'circle-radius': ['case', ['get', 'eligible'], 3, 2] as any,
          'circle-color': ['case', ['get', 'eligible'], '#10b981', '#f59e0b'] as any,
          'circle-opacity': 0.9,
        },
      });
    } else {
      (map.getSource('rep-segs') as maplibregl.GeoJSONSource).setData(graph.segments as any);
      (map.getSource('rep-buf')  as maplibregl.GeoJSONSource).setData(passings.buffer as any);
      const tagged = {
        type: 'FeatureCollection' as const,
        features: (passings.passings.features ?? []).map((f: any) => ({
          ...f,
          properties: { ...(f.properties ?? {}), eligible: eligibleSet.has(String(f.properties?.location_id ?? f.id)) },
        })),
      };
      (map.getSource('rep-dots') as maplibregl.GeoJSONSource).setData(tagged);
    }

    // Fit
    if (graph.segments.features.length) {
      const b = new maplibregl.LngLatBounds();
      for (const f of graph.segments.features) for (const c of f.geometry.coordinates) b.extend(c as [number, number]);
      if (!b.isEmpty()) map.fitBounds(b, { padding: 30, duration: 0, maxZoom: 16 });
    } else if (passings.buffer) {
      // Fallback: fit to buffer if segments haven't been loaded yet
      try {
        const b = new maplibregl.LngLatBounds();
        const walk = (coords: any) => {
          if (typeof coords[0] === 'number') b.extend(coords as [number, number]);
          else coords.forEach(walk);
        };
        const feats = passings.buffer.features ?? [passings.buffer];
        for (const f of feats) if (f.geometry) walk(f.geometry.coordinates);
        if (!b.isEmpty()) map.fitBounds(b, { padding: 30, duration: 0, maxZoom: 15 });
      } catch { /* ignore */ }
    }
    // Force a resize in case the container just became visible after data arrived
    map.resize();
  }, [mapReady, graph, passings, beadEligible]);

  if (!project) {
    return <div className="page__header"><div>Loading…</div></div>;
  }

  const pencils = bom?.pencils_against_subgrant ?? false;

  return (
    <div className="bead-report-page">
      {/* NON-PRINT toolbar */}
      <div className="bead-toolbar">
        <select className="map-select" value={selectedProjectId ?? ''} onChange={(e) => setSelectedProjectId(e.target.value)}>
          {projects.map((p: any) => (
            <option key={p.id} value={p.id}>
              {p.code} — {p.name}{p.subgrant_amount > 0 ? ` · ${fmtUSD(p.subgrant_amount)} BEAD` : ''}
            </option>
          ))}
        </select>
        <button className="btn btn--primary" onClick={() => window.print()}>🖨 Print / Save as PDF</button>
      </div>

      {/* PRINT-READY REPORT */}
      <div className="bead-report">
        <header className="bead-report__header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                width: 32, height: 32, borderRadius: 6,
                background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
                color: '#0b1220', fontWeight: 800, fontSize: 18,
                display: 'grid', placeItems: 'center',
              }}>M</span>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Milestone Build</div>
            </div>
            <h1 style={{ marginTop: 14, fontSize: 28, fontWeight: 800 }}>BEAD Territory Validation Report</h1>
            <div style={{ marginTop: 4, fontSize: 14, color: 'var(--text-muted)' }}>
              {project.name} ({project.code}) — {project.city}, {project.state}
            </div>
            {project.bead_award_round && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Award round: <b>{project.bead_award_round}</b>
                {project.deadline ? ` · Deadline ${new Date(project.deadline).toLocaleDateString()}` : ''}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Generated</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{new Date().toLocaleString()}</div>
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Client</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{project.client ?? '—'}</div>
          </div>
        </header>

        {/* Headline pencils tile */}
        {bom && bom.subgrant_amount > 0 && (
          <div className={`bead-headline ${pencils ? 'pencils-yes' : 'pencils-no'}`}>
            <div className="bead-headline__icon">{pencils ? '✓' : '✗'}</div>
            <div style={{ flex: 1 }}>
              <div className="bead-headline__big">
                {pencils ? `Project pencils with ${fmtUSD(bom.delta)} headroom` : `Over budget by ${fmtUSD(-bom.delta)}`}
              </div>
              <div className="bead-headline__sub">
                Build cost <b>{fmtUSD(bom.total_cost)}</b> against <b>{fmtUSD(bom.budget_total)}</b> budget
                ({fmtUSD(bom.subgrant_amount)} subgrant + {(bom.contingency_pct * 100).toFixed(0)}% contingency)
              </div>
            </div>
          </div>
        )}

        {/* KPI tiles */}
        {bom && (
          <div className="bead-kpis">
            <KPI label="Total cost" value={fmtUSD(bom.total_cost)} />
            <KPI label="$/eligible BSL" value={fmtUSD(bom.cost_per_eligible_passing)}
                 accent={bom.cost_per_eligible_passing < 3000 ? 'green' : 'amber'} />
            <KPI label="$/total passing" value={fmtUSD(bom.cost_per_passing)} accent="blue" />
            <KPI label="Eligible passed" value={fmtNum(bom.passings_eligible)} accent="green" />
            <KPI label="Opportunistic uplift" value={fmtNum(bom.passings_opportunistic)} accent="amber" />
            <KPI label="Plant total" value={`${Math.round(bom.total_cable_ft / 5280 * 10) / 10} mi`} />
          </div>
        )}

        {/* Map preview */}
        <section className="bead-section">
          <h2>Service area + plant</h2>
          <div ref={containerRef} className="bead-map" />
          <div className="bead-legend">
            <span><span className="dot dot--feeder" /> Plant route</span>
            <span><span className="dot dot--buffer" /> 300 ft passing buffer</span>
            <span><span className="dot dot--bead" /> BEAD eligible (Layer 1)</span>
            <span><span className="dot dot--opp" /> Opportunistic (Layer 2)</span>
          </div>
        </section>

        {/* 3-Layer comparison table */}
        {layers && (
          <section className="bead-section">
            <h2>3-layer economic comparison</h2>
            <table className="bead-compare">
              <thead>
                <tr>
                  <th></th>
                  <th>Layer 1 only<br /><small>BEAD-mandated</small></th>
                  <th>+ Layer 2<br /><small>Opportunistic</small></th>
                  <th>
                    + Layer 3 {(layers.l1_l2_l3 as any).is_estimate ? <small>(est.)</small> : <small>(committed)</small>}<br />
                    <small>
                      {(layers.l1_l2_l3 as any).zone_count > 0
                        ? `${(layers.l1_l2_l3 as any).zone_count} private-capex zone${(layers.l1_l2_l3 as any).zone_count === 1 ? '' : 's'}`
                        : 'Adjacent towns'}
                    </small>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Homes passed</td>
                  <td>{fmtNum(layers.l1.homes)}</td>
                  <td className="bead-compare__win">{fmtNum(layers.l1_l2.homes)}</td>
                  <td className="bead-compare__win">{fmtNum(layers.l1_l2_l3.homes)}</td>
                </tr>
                <tr>
                  <td>Build cost</td>
                  <td>{fmtUSD(layers.l1.cost)}</td>
                  <td>{fmtUSD(layers.l1_l2.cost)}</td>
                  <td>{fmtUSD(layers.l1_l2_l3.cost)}</td>
                </tr>
                <tr>
                  <td>$/home</td>
                  <td>{fmtUSD(layers.l1.cph)}</td>
                  <td className="bead-compare__win">{fmtUSD(layers.l1_l2.cph)}</td>
                  <td className="bead-compare__win">{fmtUSD(layers.l1_l2_l3.cph)}</td>
                </tr>
                <tr>
                  <td>Subgrant funded</td>
                  <td>{fmtUSD(layers.l1.grant_funded)}</td>
                  <td>{fmtUSD(layers.l1_l2.grant_funded)}</td>
                  <td>{fmtUSD(layers.l1_l2_l3.grant_funded)}</td>
                </tr>
                <tr>
                  <td>Net capex</td>
                  <td className={layers.l1.net_capex > 0 ? 'bead-compare__loss' : 'bead-compare__win'}>{fmtUSD(layers.l1.net_capex)}</td>
                  <td className={layers.l1_l2.net_capex > 0 ? 'bead-compare__loss' : 'bead-compare__win'}>{fmtUSD(layers.l1_l2.net_capex)}</td>
                  <td className={layers.l1_l2_l3.net_capex > 0 ? 'bead-compare__loss' : 'bead-compare__win'}>{fmtUSD(layers.l1_l2_l3.net_capex)}</td>
                </tr>
                <tr>
                  <td>5-yr revenue (PV)*</td>
                  <td style={{ color: 'var(--text-muted)' }}>—</td>
                  <td className="bead-compare__win">{fmtUSD(layers.l1_l2.revenue_pv)}</td>
                  <td className="bead-compare__win">{fmtUSD(layers.l1_l2_l3.revenue_pv)}</td>
                </tr>
              </tbody>
            </table>
            <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 8 }}>
              {(layers.l1_l2_l3 as any).is_estimate
                ? '*Revenue PV @ $85 ARPU × 35–45% take rate × 60 months. Layer 3 estimates are illustrative — run an Expansion Study on the Builder to replace with actual extension design.'
                : `*Revenue PV @ $85 ARPU × 35–45% take rate × 60 months. Layer 3 sums ${(layers.l1_l2_l3 as any).zone_count} committed expansion zone(s) from the Builder: +${fmtNum((layers.l1_l2_l3 as any).l3_homes)} addressable passings for +${fmtUSD((layers.l1_l2_l3 as any).l3_capex)} private capex.`}
            </div>

            {committedZones.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                  Committed expansion zones
                </div>
                <table className="bead-compare">
                  <thead>
                    <tr><th>Zone</th><th>Passings</th><th>Extra capex</th><th>$/passing</th><th>Payback</th></tr>
                  </thead>
                  <tbody>
                    {committedZones.map((z) => {
                      const a = z.analysis ?? {};
                      const p = a.passings ?? {};
                      const sellable = Number(p.served ?? 0) + Number(p.unknown ?? 0) + Number(p.bead_eligible ?? 0);
                      return (
                        <tr key={z.id}>
                          <td>{z.name}</td>
                          <td>{fmtNum(sellable)}</td>
                          <td>{fmtUSD(a.incremental_capex ?? 0)}</td>
                          <td>{fmtUSD(a.incremental_per_passing ?? 0)}</td>
                          <td>{a.simple_payback_months != null ? `${a.simple_payback_months.toFixed(1)} mo` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Cost breakdown */}
        {bom && (
          <section className="bead-section">
            <h2>Cost breakdown</h2>
            <table className="bead-cost">
              <tbody>
                <tr><td>Cable</td><td>{fmtNum(bom.total_cable_ft)} ft</td><td>{fmtUSD(bom.total_cable_cost)}</td></tr>
                <tr className="indent"><td>Feeder</td><td>{fmtNum(bom.feeder_ft)} ft</td><td>{fmtUSD(bom.feeder_cost)}</td></tr>
                <tr className="indent"><td>Distribution</td><td>{fmtNum(bom.distribution_ft)} ft</td><td>{fmtUSD(bom.distribution_cost)}</td></tr>
                <tr className="indent"><td>Drop</td><td>{fmtNum(bom.drop_ft)} ft</td><td>{fmtUSD(bom.drop_cost)}</td></tr>
                <tr className="indent"><td>Slack / coils</td><td>{fmtNum(bom.slack_ft)} ft</td><td>{fmtUSD(bom.slack_cost)}</td></tr>
                <tr><td>Hardware</td><td>{bom.nodes.reduce((s, n) => s + n.count, 0)} units</td><td>{fmtUSD(bom.hardware_cost)}</td></tr>
                {bom.nodes.map((n) => (
                  <tr className="indent" key={n.type}>
                    <td>{n.type.replace('_', ' ')}</td>
                    <td>{n.count} × {fmtUSD(n.unit_cost)}</td>
                    <td>{fmtUSD(n.cost)}</td>
                  </tr>
                ))}
                <tr><td>Labor</td><td></td><td>{fmtUSD(bom.labor_cost)}</td></tr>
                <tr><td>Adders (eng + permits + PM + contingency)</td><td></td><td>{fmtUSD(bom.adders_cost)}</td></tr>
                <tr className="bead-cost__total"><td>Total</td><td></td><td>{fmtUSD(bom.total_cost)}</td></tr>
              </tbody>
            </table>
          </section>
        )}

        {/* Project metadata */}
        <section className="bead-section">
          <h2>Project parameters</h2>
          <table className="bead-meta">
            <tbody>
              <tr><td>Subgrant award</td><td>{fmtUSD(project.subgrant_amount ?? 0)}</td></tr>
              <tr><td>BSL commitment</td><td>{project.bsl_commitment ?? '—'}</td></tr>
              <tr><td>Contingency</td><td>{((project.contingency_pct ?? 0.25) * 100).toFixed(0)}%</td></tr>
              <tr><td>Deadline</td><td>{project.deadline ? new Date(project.deadline).toLocaleDateString() : '—'}</td></tr>
              <tr><td>Project status</td><td>{project.status}</td></tr>
              <tr><td>Plant default placement</td><td>buried</td></tr>
              <tr><td>Splitter architecture</td><td>1×32 distributed</td></tr>
              <tr><td>Passing buffer</td><td>{bom?.passing_buffer_ft ?? 300} ft</td></tr>
            </tbody>
          </table>
        </section>

        <footer className="bead-footer">
          <div>Generated by <b>Milestone Build</b> · {new Date().toISOString().split('T')[0]}</div>
          <div>Confidential — for internal planning use only.</div>
        </footer>
      </div>
    </div>
  );
}

function KPI({ label, value, accent }: { label: string; value: string; accent?: 'green' | 'amber' | 'blue' }) {
  const color =
    accent === 'green' ? '#10b981' :
    accent === 'amber' ? '#f59e0b' :
    accent === 'blue'  ? '#0ea5e9' : 'var(--text)';
  return (
    <div className="bead-kpi">
      <div className="bead-kpi__label">{label}</div>
      <div className="bead-kpi__value" style={{ color }}>{value}</div>
    </div>
  );
}
